import { memo } from 'react';
import PropTypes from 'prop-types';

import { formatValue, WidgetPeriodChip } from '../shared';

/**
 * A grouped table — dimensions down the left, measures across (`SUMMARY_TABLE`).
 *
 * The aggregated cousin of `TableWidget`, and a separate component because the
 * two answer different questions. A table shows records; a summary table shows
 * *groups* of records, so it has three things a record table never has:
 *
 * - **`role` per column.** `DIMENSION` is what the rows are grouped by,
 *   `MEASURE` is what was computed for each group. The role decides alignment
 *   and weight, which is why it is a first-class key in the contract instead
 *   of something the renderer guesses from the values.
 * - **A totals row.** It is pinned under the body and reads as part of the
 *   table, not as another group — a total that scrolls away with the rows is
 *   a number nobody sees. A `null` in `totals` for a dimension column is left
 *   blank: summing a technician's name is not a thing.
 * - **`is_others`.** The bucket a top-N cut rolls everything else into. Drawn
 *   in italics, because "Others" is a different kind of row from "Ana" and
 *   reading it as another technician is the mistake this guards against.
 *
 * Rows arrive in the order the backend sorted them and are not re-sorted here:
 * the author chose that order in the widget's own `sort`, and a second opinion
 * from the renderer would silently override it. `sort` says *which* column
 * that was, and the header marks it — otherwise the reader sees an order with
 * no stated reason and has to guess whether it means anything.
 *
 * @param {object} props
 * @param {object} props.data `SUMMARY_TABLE`'s `columns`, `rows` and `totals`.
 * @param {{ field_key: string, direction?: 'ASC'|'DESC' }} [props.sort]
 *   `display.sort` — the column the rows arrived ordered by.
 * @param {number} [props.maxRows] Truncate the body. Totals still show.
 * @param {string} [props.className]
 */
function SummaryTableWidget({ data, sort, maxRows, className = '' }) {
  const columns = data?.columns ?? [];
  const rows = data?.rows ?? [];
  if (!columns.length || !rows.length) return null;

  const visible = maxRows ? rows.slice(0, maxRows) : rows;
  const totals = data?.totals ?? null;
  // `display.sort`, or a caller threading it through the payload until the
  // widgets are handed `display` — see `GaugeChartWidget`'s `thresholds`.
  const sortBy = sort ?? data?.sort ?? null;
  const sortMark = (column) => (
    sortBy?.field_key === column.key ? (sortBy.direction === 'ASC' ? ' ↑' : ' ↓') : ''
  );
  const isMeasure = (column) => column.role === 'MEASURE';
  const cellClass = (column) =>
    (isMeasure(column) ? 'text-right font-semibold text-content' : 'text-left text-content');

  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`.trim()}>
      <div className="scrollbar-aimsos-auto min-w-0 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-edge">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.04em] text-content-muted ${
                    isMeasure(column) ? 'text-right' : 'text-left'
                  }`}
                >
                  {`${column.label ?? column.key}${sortMark(column)}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, index) => (
              <tr
                key={row.values?.[columns[0].key] ?? index}
                className="border-b border-edge last:border-b-0"
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`px-2 py-[7px] text-xs ${cellClass(column)} ${
                      row.is_others ? 'italic text-content-muted' : ''
                    }`}
                  >
                    {formatValue(row.values?.[column.key], column.format, '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {totals ? (
            <tfoot>
              <tr className="border-t-2 border-edge">
                {columns.map((column, index) => (
                  <td
                    key={column.key}
                    className={`px-2 py-[7px] text-xs font-semibold text-content ${
                      isMeasure(column) ? 'text-right' : 'text-left'
                    }`}
                  >
                    {/* The first dimension column labels the row; the rest stay blank. */}
                    {totals[column.key] === null || totals[column.key] === undefined
                      ? (index === 0 ? 'Total' : '')
                      : formatValue(totals[column.key], column.format, '')}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <WidgetPeriodChip period={data?.period} className="self-start" />
    </div>
  );
}

SummaryTableWidget.propTypes = {
  data: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
        role: PropTypes.oneOf(['DIMENSION', 'MEASURE']),
        format: PropTypes.object,
      }),
    ).isRequired,
    rows: PropTypes.arrayOf(
      PropTypes.shape({ values: PropTypes.object, is_others: PropTypes.bool }),
    ).isRequired,
    totals: PropTypes.object,
    period: PropTypes.object,
    /** Not in `data`; see the `sort` prop. */
    sort: PropTypes.object,
  }).isRequired,
  sort: PropTypes.shape({
    field_key: PropTypes.string,
    direction: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  maxRows: PropTypes.number,
  className: PropTypes.string,
};

export default memo(SummaryTableWidget);
